/*
  barna flow — Kaching free gift, pre-chosen (sections/s4-main-product.liquid, "Bundle" settings).

  Kaching's "choose your free gift" picker always starts empty and has no setting for a default.
  Once the bundle has rendered, this opens each empty picker, chooses the first gift that is in
  stock, confirms, and puts the tier the shopper started on back — so the default tier stays the
  default and the gift is already there when they move to the tier that includes it.

  It drives Kaching's own buttons rather than writing cart properties itself, so the gift is
  recorded and discounted exactly as if the shopper had picked it. If Kaching changes its markup
  the selectors stop matching and nothing happens: the picker is simply empty, as before.
*/
(() => {
  const BAR = '.kaching-bundles__bar';
  const PICKER = '.kaching-bundles__multiple-gifts-selector';
  const OPEN = '.kaching-bundles__multiple-gifts-selector__clickable-overlay';
  const CHOSEN = '.kaching-bundles__multiple-gifts-selector__photos';
  const MODAL = '.kaching-bundles__choose-multiple-gifts-modal';
  const GIFT = '.kaching-bundles__choose-multiple-gifts__product-button';
  const CONFIRM = '.kaching-bundles__choose-multiple-gifts__footer-button--confirm';
  const CANCEL = '.kaching-bundles__choose-multiple-gifts__footer-button--cancel';
  const BUSY = 's4-gift-autopick';

  // Resolves with test()'s first truthy result, re-checked on every DOM change; null on timeout.
  const until = (root, test, timeout) =>
    new Promise((resolve) => {
      const hit = test();
      if (hit) return resolve(hit);
      const observer = new MutationObserver(() => {
        const found = test();
        if (!found) return;
        observer.disconnect();
        clearTimeout(timer);
        resolve(found);
      });
      const timer = setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeout);
      observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    });

  const selectedIndex = (block) =>
    Array.from(block.querySelectorAll(BAR)).findIndex((bar) => bar.classList.contains(`${BAR.slice(1)}--selected`));

  // Kaching mounts the picker in its own wrapper outside the bundle block, so look page-wide.
  const modalNow = () => document.querySelector(MODAL);

  async function pick(picker) {
    picker.querySelector(OPEN)?.click();
    const modal = await until(document.body, modalNow, 2000);
    if (!modal) return;
    // The gift list arrives after the picker opens (Kaching fetches the products).
    await until(modal, () => modal.querySelector(GIFT), 5000);

    // Before anything is chosen, a --disabled button is a sold-out gift.
    const gift = Array.from(modal.querySelectorAll(GIFT)).find((button) => !/--disabled|--selected/.test(button.className));
    if (gift) {
      gift.click();
      await until(modal, () => modal.querySelector(`${GIFT}--selected`), 1000);
    }
    modal.querySelector(gift ? CONFIRM : CANCEL)?.click();
    await until(document.body, () => !modalNow(), 2000);
  }

  async function preselect(block) {
    const pickers = Array.from(block.querySelectorAll(`${BAR} ${PICKER}`)).filter((picker) => !picker.querySelector(CHOSEN));
    if (pickers.length === 0) return;

    const start = selectedIndex(block);
    const scroll = window.scrollY;
    const focused = document.activeElement;
    document.documentElement.classList.add(BUSY);
    try {
      for (const picker of pickers) await pick(picker);
    } finally {
      // Never leave the picker open once it stops being hidden.
      modalNow()?.querySelector(CANCEL)?.click();
      // Opening a picker selects its tier; hand the shopper back the one they landed on.
      const bars = block.querySelectorAll(BAR);
      if (start >= 0 && selectedIndex(block) !== start) bars[start]?.querySelector('input')?.click();
      document.documentElement.classList.remove(BUSY);
      if (window.scrollY !== scroll) window.scrollTo(0, scroll);
      if (focused instanceof HTMLElement) focused.focus({ preventScroll: true });
    }
  }

  document.querySelectorAll('[data-kaching-gift-autopick]').forEach(async (slot) => {
    // Kaching renders its bars after its own script loads; wait up to 15s for them.
    const bars = await until(slot, () => slot.querySelector(`${BAR} ${PICKER}`), 15000);
    if (bars) preselect(bars.closest('.kaching-bundles') || slot);
  });
})();
